import { useState, useEffect, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, FileDown, Loader2, Send, CheckCircle2, Ban } from "lucide-react";

const EUR = (v) => (Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const DEVIS_COND = "Acompte de 50 % à la commande, solde à réception du dossier finalisé.";
const emptyLine = () => ({ id: Math.random().toString(36).slice(2), reference: "", designation: "", description: "", qte: 1, pu_ht: 0, tva: 20, remise_pct: 0 });

export async function openBillingPdf(id, filename) {
  const res = await api.get(`/billing/docs/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename || id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
}

export function PdfPreviewModal({ doc, onClose }) {
  const [pages, setPages] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/billing/docs/${doc.id}/preview`);
        if (alive) setPages(data.pages || []);
      } catch (e) { if (alive) setError(true); }
    })();
    return () => { alive = false; };
  }, [doc.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pdf-preview-modal" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 shrink-0">
          <div className="font-mono text-sm font-bold">{doc.numero || "Brouillon"} <span className="text-stone-400 font-sans font-normal">· {doc.client_nom || ""}</span></div>
          <div className="flex items-center gap-2">
            <button onClick={() => openBillingPdf(doc.id, doc.numero || "document")} data-testid="pdf-preview-download" title="Télécharger" className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 hover:bg-stone-100 px-3 py-1.5 rounded"><FileDown size={14}/> Télécharger</button>
            <button onClick={onClose} data-testid="pdf-preview-close" className="p-1.5 text-stone-500 hover:text-stone-900"><X size={18}/></button>
          </div>
        </div>
        <div className="flex-1 bg-stone-200 overflow-y-auto p-4 flex flex-col items-center gap-4">
          {error ? <div className="h-full flex items-center justify-center text-sm text-rose-600">Impossible de charger l'aperçu PDF.</div>
            : !pages ? <div className="h-full flex items-center justify-center text-stone-400"><Loader2 className="animate-spin" size={22}/></div>
            : pages.length === 0 ? <div className="text-sm text-stone-400 py-8">Document vide.</div>
            : pages.map((src, i) => <img key={i} src={src} alt={`page ${i + 1}`} data-testid={`pdf-preview-page-${i}`} className="w-full max-w-3xl shadow-lg bg-white"/>)}
        </div>
      </div>
    </div>
  );
}

export default function BillingDocEditor({ doc, projectId, societes, docType = "devis", onClose, onSaved }) {
  const type = doc?.type || docType;
  const isFacture = type.startsWith("facture");
  const [societeCode, setSocieteCode] = useState(doc?.societe_code || "PPD");
  const [objet, setObjet] = useState(doc?.objet || "");
  const [clientNom, setClientNom] = useState(doc?.client_nom || "");
  const [clientId, setClientId] = useState(doc?.client_id || null);
  const [clientVille, setClientVille] = useState(doc?.client_ville || "");
  const [clientAdresse, setClientAdresse] = useState(doc?.client_adresse || "");
  const [clientEmail, setClientEmail] = useState(doc?.client_email || "");
  const [conditions, setConditions] = useState(doc?.conditions_reglement || (type === "devis" ? DEVIS_COND : ""));
  const [dateValidite, setDateValidite] = useState(doc?.date_validite || "");
  const [dateEcheance, setDateEcheance] = useState(doc?.date_echeance || "");
  const [notes, setNotes] = useState(doc?.notes || "");
  const [lignes, setLignes] = useState(doc?.lignes?.length ? doc.lignes.map((l) => ({ ...l })) : [emptyLine()]);
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/billing/articles?societe=${societeCode}`).then(({ data }) => setArticles(data)).catch(() => {});
  }, [societeCode]);

  useEffect(() => {
    api.get(`/contacts`, { silent: true }).then(({ data }) => setContacts(data || [])).catch(() => {});
  }, []);

  const onClientName = (v) => {
    setClientNom(v);
    const c = contacts.find((x) => (x.nom || "").toLowerCase() === v.trim().toLowerCase());
    if (c) {
      setClientId(c.id);
      if (c.ville) setClientVille(c.ville);
      if (c.adresse) setClientAdresse(c.adresse);
      if (c.email) setClientEmail(c.email);
    } else {
      setClientId(null);
    }
  };

  const setLine = (id, k, v) => setLignes((p) => p.map((l) => l.id === id ? { ...l, [k]: v } : l));
  const removeLine = (id) => setLignes((p) => p.filter((l) => l.id !== id));
  const addLine = () => setLignes((p) => [...p, emptyLine()]);
  const addArticleLine = (ref) => {
    const a = articles.find((x) => x.reference === ref);
    if (!a) return;
    setLignes((p) => [...p, { ...emptyLine(), reference: a.reference, designation: a.designation, description: a.description || "", tva: a.tva, pu_ht: a.prix_ht ?? 0 }]);
  };

  const onPickArticle = (id, ref) => {
    const a = articles.find((x) => x.reference === ref);
    if (a) setLignes((p) => p.map((l) => l.id === id ? { ...l, reference: a.reference, designation: a.designation, description: a.description || l.description || "", tva: a.tva, pu_ht: a.prix_ht ?? l.pu_ht } : l));
  };

  const totals = useMemo(() => {
    let ht = 0; const tvaMap = {};
    lignes.forEach((l) => {
      const m = (Number(l.qte) || 0) * (Number(l.pu_ht) || 0) * (1 - (Number(l.remise_pct) || 0) / 100);
      ht += m; const t = Number(l.tva) || 0; tvaMap[t] = (tvaMap[t] || 0) + m * t / 100;
    });
    const tva = Object.values(tvaMap).reduce((s, x) => s + x, 0);
    return { ht, tva, ttc: ht + tva };
  }, [lignes]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        type, societe_code: societeCode, projet_id: projectId || doc?.projet_id || null,
        objet, client_nom: clientNom, client_id: clientId, client_ville: clientVille,
        client_adresse: clientAdresse, client_email: clientEmail,
        conditions_reglement: conditions,
        date_validite: dateValidite, date_echeance: dateEcheance, notes,
        lignes: lignes.filter((l) => l.designation || l.pu_ht),
      };
      if (doc?.id) await api.put(`/billing/docs/${doc.id}`, payload);
      else await api.post(`/billing/docs`, payload);
      toast.success(doc?.id ? "Document mis à jour" : "Document créé");
      onSaved();
    } catch (e) {
      /* toast via interceptor */
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" data-testid="billing-doc-editor">
      <div className="bg-white rounded-xl w-full max-w-4xl my-6 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl font-bold">{doc?.id ? `${doc.numero}` : (isFacture ? "Nouvelle facture" : "Nouveau devis")}</h3>
          <div className="flex items-center gap-2">
            {doc?.id && <button onClick={() => openBillingPdf(doc.id, doc.numero)} data-testid="doc-pdf-btn" className="flex items-center gap-1.5 text-xs font-semibold border border-stone-300 hover:bg-stone-100 px-3 py-1.5 rounded"><FileDown size={13}/> PDF</button>}
            <button onClick={onClose}><X size={18}/></button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <label className="flex flex-col text-xs font-semibold text-stone-600">Société
            <select value={societeCode} onChange={(e) => setSocieteCode(e.target.value)} data-testid="doc-societe" className="mt-1 border border-stone-300 rounded px-2 py-2 font-normal text-sm">
              {societes.map((s) => <option key={s.code} value={s.code}>{s.nom}</option>)}
            </select>
          </label>
          <label className="flex flex-col text-xs font-semibold text-stone-600">Client
            <input list="clients-list" value={clientNom} onChange={(e) => onClientName(e.target.value)} data-testid="doc-client" className="mt-1 border border-stone-300 rounded px-2 py-2 font-normal text-sm"/>
            <span className="mt-0.5 text-[10px] font-normal" data-testid="doc-client-link">{clientId ? <span className="text-emerald-600">● Lié à la fiche contact</span> : (clientNom ? <span className="text-amber-600">● Nouveau contact (créé à l'enregistrement)</span> : <span className="text-stone-400">Tapez ou choisissez un client</span>)}</span>
          </label>
          <label className="flex flex-col text-xs font-semibold text-stone-600">Ville
            <input value={clientVille} onChange={(e) => setClientVille(e.target.value)} className="mt-1 border border-stone-300 rounded px-2 py-2 font-normal text-sm"/>
          </label>
          <label className="flex flex-col text-xs font-semibold text-stone-600">{isFacture ? "Échéance" : "Validité"}
            <input type="date" value={((isFacture ? dateEcheance : dateValidite) || "").slice(0, 10)} onChange={(e) => isFacture ? setDateEcheance(e.target.value) : setDateValidite(e.target.value)} className="mt-1 border border-stone-300 rounded px-2 py-2 font-normal text-sm"/>
          </label>
        </div>
        <input value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Objet du devis" data-testid="doc-objet" className="w-full border border-stone-300 rounded px-3 py-2 text-sm mb-4"/>

        <datalist id="clients-list">
          {contacts.map((c) => <option key={c.id} value={c.nom}>{c.ville || ""}</option>)}
        </datalist>

        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-4">Conditions de règlement
          <textarea value={conditions} onChange={(e) => setConditions(e.target.value)} data-testid="doc-conditions" rows={2} className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>

        <datalist id="articles-list">
          {articles.map((a) => <option key={a.id} value={a.reference}>{a.designation}</option>)}
        </datalist>

        <div className="border border-stone-200 rounded-lg overflow-hidden mb-3">
          <div className="grid grid-cols-12 gap-1 bg-stone-100 px-2 py-1.5 text-[10px] uppercase tracking-wide font-bold text-stone-500">
            <div className="col-span-2">Réf.</div><div className="col-span-4">Désignation</div>
            <div className="col-span-1 text-right">Qté</div><div className="col-span-2 text-right">PU HT</div>
            <div className="col-span-1 text-right">TVA</div><div className="col-span-1 text-right">Montant</div><div className="col-span-1"></div>
          </div>
          {lignes.map((l) => {
            const m = (Number(l.qte) || 0) * (Number(l.pu_ht) || 0) * (1 - (Number(l.remise_pct) || 0) / 100);
            return (
              <div key={l.id} className="grid grid-cols-12 gap-1 px-2 py-1.5 border-t border-stone-100 items-center" data-testid={`doc-line-${l.id}`}>
                <input list="articles-list" value={l.reference} onChange={(e) => { setLine(l.id, "reference", e.target.value); onPickArticle(l.id, e.target.value); }} className="col-span-2 border border-stone-200 rounded px-1.5 py-1 text-xs"/>
                <div className="col-span-4 space-y-1">
                  <input value={l.designation} onChange={(e) => setLine(l.id, "designation", e.target.value)} placeholder="Désignation" className="w-full border border-stone-200 rounded px-1.5 py-1 text-xs"/>
                  <input value={l.description || ""} onChange={(e) => setLine(l.id, "description", e.target.value)} data-testid={`doc-line-desc-${l.id}`} placeholder="Description (optionnelle, modifiable)" className="w-full border border-stone-100 bg-stone-50/60 rounded px-1.5 py-1 text-[11px] text-stone-600"/>
                </div>
                <input type="number" value={l.qte} onChange={(e) => setLine(l.id, "qte", e.target.value)} className="col-span-1 border border-stone-200 rounded px-1.5 py-1 text-xs text-right"/>
                <input type="number" value={l.pu_ht} onChange={(e) => setLine(l.id, "pu_ht", e.target.value)} className="col-span-2 border border-stone-200 rounded px-1.5 py-1 text-xs text-right"/>
                <select value={l.tva} onChange={(e) => setLine(l.id, "tva", Number(e.target.value))} className="col-span-1 border border-stone-200 rounded px-1 py-1 text-xs">
                  <option value={20}>20%</option><option value={10}>10%</option><option value={5.5}>5,5%</option><option value={0}>0%</option>
                </select>
                <div className="col-span-1 text-right text-xs font-mono">{EUR(m)}</div>
                <button onClick={() => removeLine(l.id)} data-testid={`doc-line-del-${l.id}`} className="col-span-1 text-stone-400 hover:text-rose-600 flex justify-center"><Trash2 size={14}/></button>
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <button onClick={addLine} data-testid="doc-add-line" className="text-xs font-semibold text-[#C2502F] hover:text-[#a03f23] flex items-center gap-1"><Plus size={14}/> Ajouter une ligne manuelle</button>
          <select value="" onChange={(e) => { if (e.target.value) { addArticleLine(e.target.value); } }} data-testid="doc-add-article" className="text-xs border border-stone-300 rounded px-2 py-1.5 bg-white text-stone-700 max-w-xs">
            <option value="">+ Ajouter un article prérempli…</option>
            {articles.map((a) => <option key={a.id} value={a.reference}>{a.reference ? `${a.reference} · ` : ""}{a.designation}</option>)}
          </select>
        </div>

        <div className="flex justify-end mb-4">
          <div className="w-64 text-sm space-y-1">
            <div className="flex justify-between text-stone-600"><span>Total HT</span><span className="font-mono">{EUR(totals.ht)}</span></div>
            <div className="flex justify-between text-stone-600"><span>TVA</span><span className="font-mono">{EUR(totals.tva)}</span></div>
            <div className="flex justify-between font-bold text-[#C2502F] text-base border-t border-stone-200 pt-1"><span>Total TTC</span><span className="font-mono" data-testid="doc-total-ttc">{EUR(totals.ttc)}</span></div>
          </div>
        </div>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optionnel)" rows={2} className="w-full border border-stone-300 rounded px-3 py-2 text-sm mb-4"/>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded text-sm border border-stone-300 hover:bg-stone-50">Annuler</button>
          <button onClick={save} disabled={saving} data-testid="doc-save" className="px-5 py-2 rounded text-sm font-semibold bg-[#C2502F] text-white hover:bg-[#a03f23] flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

export function EmailModal({ doc, onClose, onSent }) {
  const LABEL = { devis: "Devis", facture: "Facture", facture_acompte: "Facture d'acompte", facture_solde: "Facture de solde", facture_situation: "Facture de situation", avoir: "Avoir" };
  const [to, setTo] = useState(doc.client_email || "");
  const [subject, setSubject] = useState(`${LABEL[doc.type] || "Document"} ${doc.numero || ""}`.trim());
  const [message, setMessage] = useState(`Bonjour,\n\nVeuillez trouver ci-joint ${doc.type === "devis" ? "notre devis" : "votre facture"} ${doc.numero || ""} au format PDF.\n\nBien cordialement,`);
  const [copyToMe, setCopyToMe] = useState(true);
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!to.trim()) { toast.error("Adresse email du destinataire requise"); return; }
    setSending(true);
    try {
      const payload = { to: to.trim(), subject, message, app_url: window.location.origin };
      if (!copyToMe) payload.cc = null;
      await api.post(`/billing/docs/${doc.id}/send-email`, payload);
      toast.success(`Email envoyé à ${to.trim()}`);
      onSent && onSent();
      onClose();
    } catch (e) { /* toast via interceptor */ } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="email-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto my-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">Envoyer par email · {doc.numero || "Brouillon"}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Destinataire
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-to" placeholder="client@exemple.fr" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Objet
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Message
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="email-message" rows={5} className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex items-center gap-2 text-xs text-stone-600 mb-4 cursor-pointer">
          <input type="checkbox" checked={copyToMe} onChange={(e) => setCopyToMe(e.target.checked)} data-testid="email-copy-me"/> M'envoyer une copie
        </label>
        <div className="text-[11px] text-stone-400 mb-4">Le PDF du document sera joint automatiquement.</div>
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mb-4" data-testid="email-test-notice">
          ⚠️ Mode test Resend : tant que ton domaine n'est pas vérifié sur <b>resend.com/domains</b>, l'envoi ne fonctionne que vers ta propre adresse Resend. Vérifie ton domaine pour envoyer à tes clients.
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded text-sm border border-stone-300 hover:bg-stone-50">Annuler</button>
          <button onClick={send} disabled={sending} data-testid="email-send" className="px-5 py-2 rounded text-sm font-semibold bg-[#C2502F] text-white hover:bg-[#a03f23] flex items-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Envoyer
          </button>
        </div>
      </div>
    </div>
  );
}

export const PAY_STATUTS = { a_encaisser: "À encaisser", partiel: "Partiellement réglé", paye: "Payé", en_retard: "En retard" };

const SOC_NOM = { PPD: "Pourquoi Pas Design", FM: "Franchi'Mob" };
const RELANCE_NIVEAUX = [["R1", "1re relance (courtoise)"], ["R2", "2e relance (ferme)"], ["mise_en_demeure", "Mise en demeure"]];
const PENAL = "Tout retard de paiement entraîne de plein droit l'application de pénalités calculées au taux d'intérêt légal majoré de 10 points, ainsi qu'une indemnité forfaitaire de 40 € pour frais de recouvrement (art. L.441-10 du Code de commerce).";

function relanceTemplate(niveau, doc) {
  const num = doc.numero || "";
  const soc = SOC_NOM[doc.societe_code] || "Pourquoi Pas Design";
  const j = doc.jours_retard || 0;
  const montant = (Number(doc.reste_ttc) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  if (niveau === "R1") return {
    subject: `Rappel — facture ${num} en attente de règlement`,
    message: `Bonjour,\n\nSauf erreur de notre part, la facture ${num} d'un montant restant dû de ${montant} TTC demeure impayée à ce jour (échéance dépassée de ${j} jour(s)).\n\nNous vous remercions de bien vouloir procéder à son règlement dans les meilleurs délais. Si le paiement a été effectué récemment, merci de ne pas tenir compte de ce rappel.\n\nBien cordialement,\n${soc}`,
  };
  if (niveau === "R2") return {
    subject: `2e relance — facture ${num} impayée`,
    message: `Bonjour,\n\nMalgré notre premier rappel, la facture ${num} d'un montant de ${montant} TTC reste impayée (échéance dépassée de ${j} jour(s)).\n\nNous vous demandons de procéder à son règlement sous 8 jours. À défaut, nous serons contraints d'engager une procédure de recouvrement.\n\nBien cordialement,\n${soc}`,
  };
  return {
    subject: `MISE EN DEMEURE — facture ${num}`,
    message: `Madame, Monsieur,\n\nMalgré nos relances, la facture ${num} d'un montant de ${montant} TTC demeure impayée (échéance dépassée de ${j} jour(s)).\n\nPar la présente, nous vous METTONS EN DEMEURE de régler l'intégralité de cette somme sous 8 jours à compter de la réception de ce courrier.\n\n${PENAL}\n\nÀ défaut de règlement dans ce délai, nous engagerons toute procédure de recouvrement utile, sans nouvel avis.\n\nBien cordialement,\n${soc}`,
  };
}

export function RelanceModal({ doc, onClose, onSent }) {
  const [niveau, setNiveau] = useState(doc.niveau_suggere || "R1");
  const [to, setTo] = useState(doc.client_email || "");
  const tpl = relanceTemplate(niveau, doc);
  const [subject, setSubject] = useState(tpl.subject);
  const [message, setMessage] = useState(tpl.message);
  const [copyToMe, setCopyToMe] = useState(true);
  const [sending, setSending] = useState(false);
  const onNiveau = (v) => { setNiveau(v); const t = relanceTemplate(v, doc); setSubject(t.subject); setMessage(t.message); };
  const send = async () => {
    if (!to.trim()) { toast.error("Adresse email du destinataire requise"); return; }
    setSending(true);
    try {
      const payload = { niveau, to: to.trim(), subject, message };
      if (!copyToMe) payload.cc = null;
      await api.post(`/billing/docs/${doc.id}/relance`, payload);
      toast.success(`Relance envoyée à ${to.trim()}`);
      onSent && onSent();
      onClose();
    } catch (e) { /* toast via interceptor */ } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="relance-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold">Relancer · {doc.numero}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <p className="text-xs text-stone-500 mb-4">Retard de {doc.jours_retard} jour(s) · {(Number(doc.reste_ttc) || 0).toLocaleString("fr-FR")} € TTC dû</p>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Niveau de relance
          <select value={niveau} onChange={(e) => onNiveau(e.target.value)} data-testid="relance-niveau" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm">
            {RELANCE_NIVEAUX.map(([k, lbl]) => <option key={k} value={k}>{lbl}{doc.niveau_suggere === k ? " (conseillé)" : ""}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Destinataire
          <input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="relance-to" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Objet
          <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="relance-subject" className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex flex-col text-xs font-semibold text-stone-600 mb-3">Message
          <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="relance-message" rows={8} className="mt-1 border border-stone-300 rounded px-3 py-2 font-normal text-sm"/>
        </label>
        <label className="flex items-center gap-2 text-xs text-stone-600 mb-4 cursor-pointer">
          <input type="checkbox" checked={copyToMe} onChange={(e) => setCopyToMe(e.target.checked)} data-testid="relance-copy-me"/> M'envoyer une copie
        </label>
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-3 py-2 mb-4">⚠️ Mode test Resend : l'envoi ne fonctionne que vers ta propre adresse Resend tant que ton domaine n'est pas vérifié. La facture PDF est jointe automatiquement.</div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded text-sm border border-stone-300 hover:bg-stone-50">Annuler</button>
          <button onClick={send} disabled={sending} data-testid="relance-send" className="px-5 py-2 rounded text-sm font-semibold bg-[#C2502F] text-white hover:bg-[#a03f23] flex items-center gap-2 disabled:opacity-50">
            {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} Envoyer la relance
          </button>
        </div>
      </div>
    </div>
  );
}
export const PAY_CLS = { a_encaisser: "bg-stone-100 text-stone-600", partiel: "bg-amber-50 text-amber-700", paye: "bg-emerald-50 text-emerald-700", en_retard: "bg-rose-50 text-rose-700" };
export const FAC_TYPE_LABEL = { facture: "Facture", facture_acompte: "Facture d'acompte", facture_solde: "Facture de solde", facture_situation: "Facture de situation", avoir: "Avoir" };

export function ConvertMenu({ doc, onDone }) {
  const [open, setOpen] = useState(false);
  const run = async (mode) => {
    let pct = null;
    if (mode === "acompte" || mode === "avancement") {
      const v = prompt(`Pourcentage du devis à facturer (${mode}) ?`, mode === "acompte" ? "30" : "50");
      if (v === null) return;
      pct = Number(v);
    }
    try {
      await api.post(`/billing/docs/${doc.id}/convert`, { mode, pct });
      toast.success("Facture créée depuis le devis");
      setOpen(false); onDone();
    } catch (e) { /* toast via interceptor */ }
  };
  return (
    <div className="relative inline-block">
      <button onClick={() => setOpen((o) => !o)} data-testid={`convert-btn-${doc.id}`} className="px-2.5 py-1.5 rounded text-xs font-semibold bg-[#C2502F] text-white hover:bg-[#a03f23] flex items-center gap-1">→ Facture</button>
      {open && (
        <div className="absolute right-0 mt-1 z-20 bg-white border border-stone-200 rounded-lg shadow-lg w-48 py-1 text-sm" data-testid={`convert-menu-${doc.id}`}>
          <button onClick={() => run("globale")} className="block w-full text-left px-3 py-2 hover:bg-stone-50">Facture globale (100%)</button>
          <button onClick={() => run("acompte")} className="block w-full text-left px-3 py-2 hover:bg-stone-50">Facture d'acompte (%)</button>
          <button onClick={() => run("avancement")} className="block w-full text-left px-3 py-2 hover:bg-stone-50">Facture d'avancement (%)</button>
          <button onClick={() => run("solde")} className="block w-full text-left px-3 py-2 hover:bg-stone-50">Facture de solde (reste)</button>
        </div>
      )}
    </div>
  );
}

export function PaymentModal({ doc, onClose, onSaved }) {
  const [d, setD] = useState(doc);
  const [montant, setMontant] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [moyen, setMoyen] = useState("Virement");
  const reste = (Number(d.total_ttc) || 0) - (Number(d.montant_paye) || 0);
  const add = async () => {
    if (!montant || Number(montant) <= 0) { alert("Montant invalide"); return; }
    const { data } = await api.post(`/billing/docs/${doc.id}/payments`, { montant: Number(montant), date, moyen });
    setD(data); setMontant(""); onSaved && onSaved();
  };
  const del = async (pid) => { const { data } = await api.delete(`/billing/docs/${doc.id}/payments/${pid}`); setD(data); onSaved && onSaved(); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="payment-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto my-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl font-bold">Paiements · {d.numero}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-sm mb-4">
          <div><div className="text-[10px] uppercase text-stone-400 font-bold">Total TTC</div><div className="font-mono font-semibold">{EUR(d.total_ttc)}</div></div>
          <div><div className="text-[10px] uppercase text-stone-400 font-bold">Payé</div><div className="font-mono font-semibold text-emerald-700">{EUR(d.montant_paye)}</div></div>
          <div><div className="text-[10px] uppercase text-stone-400 font-bold">Reste</div><div className="font-mono font-semibold text-amber-700">{EUR(reste)}</div></div>
        </div>
        {(d.paiements || []).length > 0 && (
          <div className="space-y-1 mb-4">
            {d.paiements.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm border-b border-stone-100 py-1" data-testid={`payment-${p.id}`}>
                <span className="text-stone-500 w-24">{p.date}</span>
                <span className="font-mono font-semibold flex-1">{EUR(p.montant)}</span>
                <span className="text-xs text-stone-500">{p.moyen}</span>
                <button onClick={() => del(p.id)} className="text-stone-400 hover:text-rose-600"><Trash2 size={13}/></button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-12 gap-2 items-end bg-stone-50 border border-stone-100 rounded p-3">
          <label className="col-span-4 flex flex-col text-[10px] uppercase font-bold text-stone-500">Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 border border-stone-300 rounded px-2 py-1.5 text-sm font-normal"/></label>
          <label className="col-span-3 flex flex-col text-[10px] uppercase font-bold text-stone-500">Montant<input type="number" value={montant} onChange={(e) => setMontant(e.target.value)} data-testid="payment-montant" className="mt-1 border border-stone-300 rounded px-2 py-1.5 text-sm font-normal"/></label>
          <label className="col-span-3 flex flex-col text-[10px] uppercase font-bold text-stone-500">Moyen<select value={moyen} onChange={(e) => setMoyen(e.target.value)} className="mt-1 border border-stone-300 rounded px-2 py-1.5 text-sm font-normal"><option>Virement</option><option>Chèque</option><option>CB</option><option>Espèces</option></select></label>
          <button onClick={add} data-testid="payment-add" className="col-span-2 bg-[#C2502F] text-white rounded py-2 text-sm font-semibold">Valider</button>
        </div>
      </div>
    </div>
  );
}
